import React, { useEffect, useState } from "react";
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  CirclePower,
  Info,
  List,
  MoreHorizontal,
  Pause,
  Play,
  RotateCcw,
  Settings2,
  Volume1,
  Volume2,
  X,
} from "lucide-react";

const utilityRoots = () => Array.from(document.querySelectorAll(".sltv-guide-prefs-tools, .sltv-guide-owner-tools"));

function mobileUtilityLabel(button) {
  return `${button.getAttribute("aria-label") || ""} ${button.getAttribute("title") || ""} ${button.textContent || ""}`
    .replace(/\s+/g, " ").trim().toUpperCase();
}

function findMobileUtilityButton(kind) {
  const buttons = utilityRoots().flatMap((root) => Array.from(root.querySelectorAll("button, [role=button]")))
    .filter((button) => !button.disabled && button.getAttribute("aria-disabled") !== "true");
  const exact = {
    prefs: [/^PREFS$/, /^PREFERENCES$/, /^VIEWER SETTINGS$/],
    share: [/^SHARE$/],
    trophies: [/^TROPHIES$/, /^ACHIEVEMENTS$/],
    owner: [/^RESTORE OWNER$/, /^RESTORE OWNER ACCESS$/, /^OWNER ACCESS$/, /^OWNER MODE$/],
  };
  const fallback = {
    prefs: /(PREFS|PREFERENCES|VIEWER SETTINGS)/,
    share: /\bSHARE\b/,
    trophies: /(TROPH|ACHIEV)/,
    owner: /(RESTORE.*OWNER|OWNER.*RESTORE|OWNER ACCESS|OWNER MODE|RESTORE ACCESS)/,
  };
  const exactMatch = buttons.find((button) => (exact[kind] || []).some((re) => re.test(mobileUtilityLabel(button))));
  if (exactMatch) return exactMatch;
  return buttons.find((button) => (fallback[kind] || /$a/).test(mobileUtilityLabel(button))) || null;
}

function TouchButton({ onClick, disabled = false, label, children, attention = false, className = "", testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      className={`sltv-touch-button ${attention ? "sltv-touch-button-attention" : ""} ${className}`}
      aria-label={label}
    >
      {children}
      <span>{label}</span>
    </button>
  );
}

export default function MobileTouchRemote({
  power,
  paused,
  volume,
  updatesAvailable = false,
  guideMode = false,
  onChannelDown,
  onChannelUp,
  onGuideLeft,
  onGuideRight,
  onPlayPause,
  onVolumeDown,
  onVolumeUp,
  onGuide,
  onSelect,
  onLast,
  onPictureMode,
  onUpdates,
  onPower,
}) {
  const [moreOpen, setMoreOpen] = useState(false);
  const [mobileUtilities, setMobileUtilities] = useState({ prefs: false, share: false, trophies: false, owner: false });

  useEffect(() => {
    const scan = () => setMobileUtilities({
      prefs: !!findMobileUtilityButton("prefs"),
      share: !!findMobileUtilityButton("share"),
      trophies: !!findMobileUtilityButton("trophies"),
      owner: !!findMobileUtilityButton("owner"),
    });
    scan();
    const observer = new MutationObserver(scan);
    observer.observe(document.body, { childList: true, subtree: true, attributes: true });
    return () => observer.disconnect();
  }, []);

  const runMobileUtility = (kind) => {
    const events = {
      prefs: "sltv:mobile-open-prefs",
      share: "sltv:mobile-share",
      trophies: "sltv:mobile-open-trophies",
      owner: "sltv:mobile-restore-owner",
    };
    const eventName = events[kind];
    if (!eventName) return;
    window.dispatchEvent(new CustomEvent(eventName));
  };

  return (
    <section className="sltv-mobile-touch-remote" data-testid="mobile-touch-remote" aria-label="SCARTLEAD.TV touch controls">
      <div className="sltv-touch-remote-head">
        <div>
          <div className="sltv-touch-remote-kicker">SCARTLEAD.TV</div>
          <div className="sltv-touch-remote-title">TOUCH CONTROL</div>
        </div>
        <button
          type="button"
          className={`sltv-touch-status sltv-touch-power-status ${power ? "is-live" : ""}`}
          onClick={onPower}
          data-testid="mobile-power-status"
          aria-label={power ? "Turn television off" : "Turn television on"}
        >
          <CirclePower size={15} />
          <span className="sltv-touch-status-dot" />
          {power ? "LIVE" : "OFF"}
        </button>
      </div>

      {guideMode ? (
        <div className="sltv-touch-guide-mode" data-testid="mobile-guide-navigation">
          <div className="sltv-touch-guide-banner">
            <span>GUIDE NAVIGATION</span>
            <span>TOUCH MODE</span>
          </div>

          <div className="sltv-touch-guide-grid">
            <TouchButton onClick={onGuideLeft} disabled={!power} label="PROGRAMME ←" testId="mobile-guide-left">
              <ChevronLeft size={24} />
            </TouchButton>

            <TouchButton onClick={onChannelUp} disabled={!power} label="CHANNEL ↑" testId="mobile-guide-up">
              <ChevronUp size={24} />
            </TouchButton>

            <TouchButton onClick={onChannelDown} disabled={!power} label="CHANNEL ↓" testId="mobile-guide-down">
              <ChevronDown size={24} />
            </TouchButton>

            <TouchButton onClick={onGuideRight} disabled={!power} label="PROGRAMME →" testId="mobile-guide-right">
              <ChevronRight size={24} />
            </TouchButton>
          </div>

          <TouchButton onClick={onGuide} disabled={!power} label="CLOSE GUIDE" className="sltv-touch-button-primary sltv-touch-guide-close" testId="mobile-guide-close">
            <X size={21} />
          </TouchButton>
        </div>
      ) : (
        <>
      <div className="sltv-touch-primary-grid">
        <TouchButton onClick={onChannelDown} disabled={!power} label="CH −" testId="mobile-channel-down">
          <ChevronDown size={22} />
        </TouchButton>

        <TouchButton onClick={onPlayPause} disabled={!power} label={paused ? "PLAY" : "PAUSE"} className="sltv-touch-button-primary" testId="mobile-play-pause">
          {paused ? <Play size={24} fill="currentColor" /> : <Pause size={24} fill="currentColor" />}
        </TouchButton>

        <TouchButton onClick={onChannelUp} disabled={!power} label="CH +" testId="mobile-channel-up">
          <ChevronUp size={22} />
        </TouchButton>

        <TouchButton onClick={onVolumeDown} disabled={!power || volume <= 0} label="VOL −" testId="mobile-volume-down">
          <Volume1 size={21} />
        </TouchButton>

        <TouchButton onClick={onSelect} disabled={!power} label="INFO" testId="mobile-info">
          <Info size={22} />
        </TouchButton>

        <TouchButton onClick={onVolumeUp} disabled={!power || volume >= 10} label="VOL +" testId="mobile-volume-up">
          <Volume2 size={21} />
        </TouchButton>
      </div>

      <div className="sltv-touch-secondary-row sltv-touch-secondary-row-playback">
        <TouchButton onClick={onGuide} disabled={!power} label="GUIDE" testId="mobile-guide">
          <List size={20} />
        </TouchButton>

        <TouchButton onClick={() => setMoreOpen((v) => !v)} label={moreOpen ? "CLOSE" : "MORE"} className={moreOpen ? "sltv-touch-button-primary" : ""} testId="mobile-more">
          {moreOpen ? <X size={20} /> : <MoreHorizontal size={22} />}
        </TouchButton>
      </div>

        </>
      )}

      {!guideMode && moreOpen && (
        <div className="sltv-touch-more-panel" data-testid="mobile-more-panel">
          {mobileUtilities.prefs && (
            <TouchButton onClick={() => runMobileUtility("prefs")} label="PREFS" testId="mobile-prefs">
              <span className="sltv-touch-utility-glyph">⚙</span>
            </TouchButton>
          )}
          {mobileUtilities.share && (
            <TouchButton onClick={() => runMobileUtility("share")} label="SHARE" testId="mobile-share">
              <span className="sltv-touch-utility-glyph">↗</span>
            </TouchButton>
          )}
          {mobileUtilities.trophies && (
            <TouchButton onClick={() => runMobileUtility("trophies")} label="TROPHIES" testId="mobile-trophies">
              <span className="sltv-touch-utility-glyph">★</span>
            </TouchButton>
          )}
          {mobileUtilities.owner && (
            <TouchButton onClick={() => runMobileUtility("owner")} label="RESTORE OWNER" testId="mobile-restore-owner">
              <span className="sltv-touch-utility-glyph">⌘</span>
            </TouchButton>
          )}
          <TouchButton onClick={onLast} disabled={!power} label="LAST" testId="mobile-last">
            <RotateCcw size={20} />
          </TouchButton>

          <TouchButton onClick={onPictureMode} disabled={!power} label="PICTURE" testId="mobile-picture">
            <Settings2 size={20} />
          </TouchButton>

          <TouchButton onClick={onUpdates} disabled={!power} label="UPDATES" attention={updatesAvailable} testId="mobile-updates">
            <span className="sltv-touch-updates-glyph">↻</span>
          </TouchButton>

          <TouchButton onClick={onPower} label={power ? "POWER OFF" : "POWER ON"} className="sltv-touch-button-power" testId="mobile-power">
            <CirclePower size={22} />
          </TouchButton>
        </div>
      )}
    </section>
  );
}
